import React, { useEffect } from 'react';
import { usePublicWebsiteData, googleDriveToDirectUrl } from '../controllers/website_crm.controller';
import { IconBrandFacebook, IconBrandInstagram, IconBrandWhatsapp, IconCheck, IconChevronRight, IconMail, IconPhone, IconStar } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import ChatWidget from '../components/ChatWidget';

export default function PublicWebsitePage() {
  const { data, isLoading, error } = usePublicWebsiteData();

  if (isLoading) return <div className="min-h-screen flex items-center justify-center"><span className="loading loading-spinner loading-lg text-green-600"></span></div>;
  if (error || !data) return <div className="min-h-screen flex items-center justify-center">Error loading website data</div>;

  const { settings, packages, products, stories, reviews, superadminSettings } = data;
  const primaryColor = settings?.primary_color || '#16a34a';
  const secondaryColor = settings?.secondary_color || '#1e40af';

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  // Helper to extract YouTube ID
  const getYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&]{11})/);
    return match ? match[1] : null;
  };

  // Dynamic App Download & Manual Links
  const appLinks = (() => {
    if (superadminSettings?.appLinks && Array.isArray(superadminSettings.appLinks)) {
      return superadminSettings.appLinks;
    }
    if (superadminSettings?.APP_DOWNLOAD_LINKS) {
      try {
        const parsed = typeof superadminSettings.APP_DOWNLOAD_LINKS === 'string'
          ? JSON.parse(superadminSettings.APP_DOWNLOAD_LINKS)
          : superadminSettings.APP_DOWNLOAD_LINKS;
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      } catch(e) {}
    }
    // Legacy fallback
    return [
      { id: "captain_app", name: "Captain App", download_link: superadminSettings?.CAPTAIN_APP_DOWNLOAD_LINK, manual_link: superadminSettings?.CAPTAIN_APP_MANUAL_LINK },
      { id: "waiter_app", name: "Waiter App", download_link: superadminSettings?.WAITER_APP_DOWNLOAD_LINK, manual_link: superadminSettings?.WAITER_APP_MANUAL_LINK },
      { id: "printer_driver", name: "Printer Driver", download_link: superadminSettings?.PRINTER_DRIVER_DOWNLOAD_LINK, manual_link: superadminSettings?.PRINTER_DRIVER_MANUAL_LINK },
      { id: "windows_app", name: "Windows App", download_link: superadminSettings?.WINDOWS_APP_DOWNLOAD_LINK, manual_link: superadminSettings?.WINDOWS_APP_MANUAL_LINK },
      { id: "mac_app", name: "Mac App", download_link: superadminSettings?.MAC_APP_DOWNLOAD_LINK, manual_link: superadminSettings?.MAC_APP_MANUAL_LINK },
      { id: "linux_app", name: "Linux App", download_link: superadminSettings?.LINUX_APP_DOWNLOAD_LINK, manual_link: superadminSettings?.LINUX_APP_MANUAL_LINK },
    ];
  })();

  const visibleLinks = (appLinks || []).filter(item => 
    (item.download_link && item.download_link.trim() !== '') || 
    (item.manual_link && item.manual_link.trim() !== '')
  );

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-gray-800 tracking-tight">
      {/* Navbar */}
      <nav className="fixed w-full bg-slate-900 text-white shadow-md z-50 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20">
            <div className="flex items-center">
              {settings?.logo_url ? (
                <img src={googleDriveToDirectUrl(settings.logo_url)} alt={settings?.site_name} className="h-10 object-contain" />
              ) : (
                <span className="text-2xl font-extrabold tracking-tight" style={{ color: primaryColor }}>{settings?.site_name || 'Website'}</span>
              )}
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('home')} className="text-slate-300 hover:text-white font-medium text-sm tracking-wide">HOME</button>
              {products?.length > 0 && <button onClick={() => scrollToSection('products')} className="text-slate-300 hover:text-white font-medium text-sm tracking-wide">PRODUCTS</button>}
              {packages?.length > 0 && <button onClick={() => scrollToSection('pricing')} className="text-slate-300 hover:text-white font-medium text-sm tracking-wide">PRICING</button>}
              {stories?.length > 0 && <button onClick={() => scrollToSection('stories')} className="text-slate-300 hover:text-white font-medium text-sm tracking-wide">STORIES</button>}
              {reviews?.length > 0 && <button onClick={() => scrollToSection('reviews')} className="text-slate-300 hover:text-white font-medium text-sm tracking-wide">REVIEWS</button>}
              {visibleLinks.length > 0 && <button onClick={() => scrollToSection('software')} className="text-slate-300 hover:text-white font-medium text-sm tracking-wide">SOFTWARE</button>}
              <button onClick={() => scrollToSection('contact')} className="text-slate-300 hover:text-white font-medium text-sm tracking-wide">CONTACT</button>
              <Link to="/login" className="px-5 py-2 text-white text-sm font-bold rounded shadow-lg transition-colors hover:opacity-90" style={{ backgroundColor: primaryColor }}>
                PORTAL LOGIN
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="pt-32 pb-24 px-4 sm:px-6 lg:px-8 min-h-[80vh] flex items-center relative overflow-hidden bg-slate-900 text-white border-b-8" style={{ borderBottomColor: primaryColor }}>
        {/* Background elements */}
        <div className="absolute top-0 left-0 w-full h-full opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
        
        <div className="max-w-7xl mx-auto w-full relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <span className="inline-block px-4 py-1.5 rounded-full text-sm font-semibold mb-6 shadow-sm" style={{ backgroundColor: `${primaryColor}20`, color: primaryColor }}>
                {settings?.tagline || 'Welcome to our platform'}
              </span>
              <h1 className="text-5xl md:text-6xl font-black tracking-tight leading-tight mb-6">
                {settings?.hero_title || 'Enterprise Solutions for a Modern World'}
              </h1>
              <p className="text-xl text-slate-300 mb-10 leading-relaxed max-w-2xl mx-auto lg:mx-0 font-light">
                {settings?.hero_subtitle || 'Discover the perfect solution tailored to your needs. Efficient, powerful, and built to scale with your organization.'}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link to="/register" className="px-8 py-4 text-white text-lg font-bold shadow-xl transition-all hover:opacity-90 flex items-center justify-center gap-2 rounded" style={{ backgroundColor: primaryColor }}>
                  GET STARTED <IconChevronRight size={20} />
                </Link>
                {settings?.whatsapp && (
                  <a href={`https://wa.me/${settings.whatsapp}`} target="_blank" rel="noopener noreferrer" className="px-8 py-4 text-white bg-slate-800 text-lg font-bold border border-slate-700 shadow-sm transition-all hover:bg-slate-700 flex items-center justify-center gap-2 rounded">
                    <IconBrandWhatsapp size={20} className="text-green-500" /> CONTACT SALES
                  </a>
                )}
              </div>
            </div>
            
            <div className="relative">
              {settings?.hero_image_url ? (
                <div className="relative shadow-2xl border border-slate-700 bg-slate-800 p-2 rounded">
                  <img src={googleDriveToDirectUrl(settings.hero_image_url)} alt="Hero" className="w-full h-auto object-cover rounded-sm" />
                </div>
              ) : (
                <div className="relative shadow-2xl border border-slate-700 bg-slate-800 p-2 aspect-video rounded flex items-center justify-center">
                  <span className="text-slate-500 font-medium tracking-widest text-sm uppercase">No Image Set</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      {products?.length > 0 && (
        <section id="products" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-4 tracking-tight uppercase">Corporate Solutions</h2>
              <div className="w-16 h-1 mx-auto mb-6" style={{ backgroundColor: primaryColor }}></div>
              <p className="text-lg text-slate-600">Enterprise-grade products designed for maximum reliability.</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {products.map(product => (
                <div key={product.id} className="group border border-slate-200 bg-white shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col rounded-sm">
                  {product.image_url ? (
                    <div className="relative h-64 overflow-hidden bg-gray-100">
                      <img src={googleDriveToDirectUrl(product.image_url)} alt={product.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      {product.is_featured === 1 && (
                        <div className="absolute top-4 left-4 px-3 py-1 bg-yellow-400 text-yellow-900 text-xs font-bold rounded-full shadow-sm">
                          FEATURED
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="h-64 bg-gray-50 flex items-center justify-center text-gray-400">No Image</div>
                  )}
                  <div className="p-8 flex-1 flex flex-col">
                    <div className="text-sm font-medium text-gray-500 mb-2">{product.category || 'Product'}</div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{product.name}</h3>
                    <p className="text-gray-600 mb-6 flex-1">{product.description}</p>
                    <div className="flex items-end justify-between mt-auto">
                      <div>
                        <span className="text-sm text-gray-500 block mb-1">Starting from</span>
                        <span className="text-3xl font-black" style={{ color: primaryColor }}>रु{product.price}</span>
                      </div>
                      {settings?.whatsapp && (
                         <a href={`https://wa.me/${settings.whatsapp}?text=I'm interested in the ${product.name}`} target="_blank" rel="noopener noreferrer" className="p-3 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-xl transition-colors">
                           <IconChevronRight size={24} />
                         </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Packages Section */}
      {packages?.length > 0 && (
        <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-4 tracking-tight uppercase">Service Plans</h2>
              <div className="w-16 h-1 mx-auto mb-6" style={{ backgroundColor: primaryColor }}></div>
              <p className="text-lg text-slate-600">Transparent enterprise pricing that scales with your growth.</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {packages.map(pkg => (
                <div key={pkg.id} className={`relative p-8 flex flex-col bg-white border transition-all duration-300 rounded-sm ${pkg.is_popular ? 'shadow-xl scale-105 border-t-4 z-10' : 'shadow hover:shadow-md border-slate-200'}`} style={{ borderTopColor: pkg.is_popular ? primaryColor : undefined }}>
                  {pkg.is_popular === 1 && (
                    <div className="absolute top-0 right-0 px-3 py-1 bg-slate-900 text-xs font-bold text-white tracking-widest uppercase rounded-bl-sm">
                      RECOMMENDED
                    </div>
                  )}
                  
                  <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">{pkg.name}</h3>
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-5xl font-black text-gray-900">{pkg.currency}{pkg.price}</span>
                      {pkg.duration && <span className="text-gray-500 font-medium">/{pkg.duration}</span>}
                    </div>
                  </div>
                  
                  <div className="space-y-4 mb-8 flex-1">
                    {pkg.features?.split('\n').filter(f => f.trim()).map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <div className="mt-1 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: `${primaryColor}20`, color: primaryColor }}>
                          <IconCheck size={12} stroke={3} />
                        </div>
                        <span className="text-gray-600">{feature.trim()}</span>
                      </div>
                    ))}
                  </div>
                  
                  <a href={settings?.whatsapp ? `https://wa.me/${settings.whatsapp}?text=I want to subscribe to the ${pkg.name} plan` : '#'} target="_blank" rel="noopener noreferrer" className="w-full py-4 text-center text-white font-bold rounded-xl transition-all hover:shadow-lg" style={{ backgroundColor: pkg.is_popular ? primaryColor : '#4b5563' }}>
                    Choose Plan
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Stories Section */}
      {stories?.length > 0 && (
        <section id="stories" className="py-24 px-4 sm:px-6 lg:px-8 bg-white border-t border-slate-200">
          <div className="max-w-7xl mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-4 tracking-tight uppercase">Client Stories</h2>
              <div className="w-16 h-1 mx-auto mb-6" style={{ backgroundColor: primaryColor }}></div>
              <p className="text-lg text-slate-600">See how leading enterprises transform with our technology.</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {stories.map(story => {
                const ytId = getYouTubeId(story.youtube_url);
                if (!ytId && !story.image_url) return null;
                
                return (
                  <div key={story.id} className="group overflow-hidden shadow border border-slate-200 bg-white rounded-sm hover:shadow-xl transition-all duration-300">
                    <div className="aspect-video relative overflow-hidden bg-slate-100">
                      {ytId ? (
                        <iframe 
                          src={`https://www.youtube.com/embed/${ytId}?rel=0`} 
                          className="w-full h-full" 
                          frameBorder="0" 
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                          allowFullScreen>
                        </iframe>
                      ) : (
                        <img src={googleDriveToDirectUrl(story.image_url)} alt={story.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      )}
                    </div>
                    <div className="p-6">
                      <h3 className="text-lg font-bold text-slate-900 mb-2 line-clamp-2" style={{ color: primaryColor }}>{story.title}</h3>
                      {story.description && <p className="text-slate-600 text-sm line-clamp-3 leading-relaxed">{story.description}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Reviews Section */}
      {reviews?.length > 0 && (
        <section id="reviews" className="py-24 px-4 sm:px-6 lg:px-8 bg-slate-50 border-t border-slate-200">
          <div className="max-w-7xl mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-4 tracking-tight uppercase">Client Testimonials</h2>
              <div className="w-16 h-1 mx-auto mb-6" style={{ backgroundColor: primaryColor }}></div>
              <p className="text-lg text-slate-600">Trusted by industry leaders worldwide.</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {reviews.map(review => (
                <div key={review.id} className="bg-white p-8 border border-slate-200 shadow-sm relative rounded-sm">
                  {/* Quote icon mark */}
                  <div className="absolute top-6 right-8 text-6xl text-slate-100 font-serif leading-none">"</div>
                  
                  <div className="flex gap-1 mb-6 relative z-10">
                    {[...Array(5)].map((_, i) => (
                      <IconStar key={i} size={16} className={i < review.rating ? 'text-yellow-400 fill-current' : 'text-slate-200'} />
                    ))}
                  </div>
                  
                  <p className="text-slate-700 text-sm leading-relaxed mb-8 relative z-10">"{review.comment}"</p>
                  
                  <div className="flex items-center gap-4 relative z-10 pt-4 border-t border-slate-100">
                    {review.customer_photo_url ? (
                      <img src={googleDriveToDirectUrl(review.customer_photo_url)} alt={review.customer_name} className="w-12 h-12 rounded-full object-cover shadow-sm grayscale hover:grayscale-0 transition-all" onError={(e) => {e.target.src = 'https://ui-avatars.com/api/?name=' + review.customer_name}} />
                    ) : (
                      <div className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-sm bg-slate-100 text-slate-600">
                        {review.customer_name.charAt(0)}
                      </div>
                    )}
                    <div>
                      <h4 className="font-bold text-slate-900 text-sm tracking-wide">{review.customer_name}</h4>
                      <p className="text-xs text-slate-500 uppercase tracking-widest mt-1">{review.designation}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}


      {/* Software Section */}
      {visibleLinks.length > 0 && (
        <section id="software" className="py-24 px-4 sm:px-6 lg:px-8 bg-white border-t border-slate-200">
          <div className="max-w-7xl mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-4 tracking-tight uppercase">Software & Apps</h2>
              <div className="w-16 h-1 mx-auto mb-6" style={{ backgroundColor: primaryColor }}></div>
              <p className="text-lg text-slate-600">Download the latest software, apps, and manuals for your devices.</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {visibleLinks.map((item, index) => (
                <div key={item.id || index} className="bg-slate-50 p-8 border border-slate-200 rounded-lg shadow-sm hover:shadow-md transition-shadow flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6 text-white shadow-sm" style={{ backgroundColor: primaryColor }}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-4">{item.name || "App / Software"}</h3>
                  <div className="mt-auto flex gap-3 w-full">
                    {item.download_link && (
                      <a 
                        href={item.download_link} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="flex-1 py-2 px-4 rounded text-white font-medium text-sm transition-opacity hover:opacity-90 flex items-center justify-center gap-1" 
                        style={{ backgroundColor: primaryColor }}
                      >
                        Download
                      </a>
                    )}
                    {item.manual_link && (
                      <a 
                        href={item.manual_link} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="flex-1 py-2 px-4 rounded bg-slate-200 text-slate-700 font-medium text-sm hover:bg-slate-300 transition-colors flex items-center justify-center gap-1"
                      >
                        Manual
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}

      <footer className="bg-gray-900 text-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-12 border-b border-gray-800 pb-12 mb-8">
          <div className="md:col-span-1">
            <div className="flex items-center mb-6">
              {settings?.logo_url ? (
                <img src={googleDriveToDirectUrl(settings.logo_url)} alt={settings?.site_name} className="h-12 object-contain" />
              ) : (
                <span className="text-2xl font-bold">{settings?.site_name || 'Website'}</span>
              )}
            </div>
            <p className="text-gray-400 leading-relaxed mb-6">
              {settings?.description || 'Your all-in-one platform for managing and growing your business effectively.'}
            </p>
            <div className="flex gap-4">
              {settings?.facebook_url && (
                <a href={settings.facebook_url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-blue-600 transition-colors">
                  <IconBrandFacebook size={20} />
                </a>
              )}
              {settings?.instagram_url && (
                <a href={settings.instagram_url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-pink-600 transition-colors">
                  <IconBrandInstagram size={20} />
                </a>
              )}
              {settings?.whatsapp && (
                <a href={`https://wa.me/${settings.whatsapp}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-green-500 transition-colors">
                  <IconBrandWhatsapp size={20} />
                </a>
              )}
            </div>
          </div>
          
          <div>
            <h4 className="text-lg font-bold mb-6">Quick Links</h4>
            <ul className="space-y-4 text-gray-400">
              <li><button onClick={() => scrollToSection('home')} className="hover:text-white transition-colors">Home</button></li>
              {products?.length > 0 && <li><button onClick={() => scrollToSection('products')} className="hover:text-white transition-colors">Products</button></li>}
              {packages?.length > 0 && <li><button onClick={() => scrollToSection('pricing')} className="hover:text-white transition-colors">Pricing</button></li>}
              {stories?.length > 0 && <li><button onClick={() => scrollToSection('stories')} className="hover:text-white transition-colors">Success Stories</button></li>}
              <li><Link to="/login" className="hover:text-white transition-colors">Login / Dashboard</Link></li>
              <li><a href="/website" className="hover:text-white transition-colors">Website CRM</a></li>
            </ul>
          </div>
          
          <div id="contact">
            <h4 className="text-lg font-bold mb-6">Contact Us</h4>
            <ul className="space-y-4 text-gray-400">
              {settings?.phone && (
                <li className="flex items-center gap-3">
                  <IconPhone size={18} className="text-gray-500" /> 
                  <a href={`tel:${settings.phone}`} className="hover:text-white transition-colors">{settings.phone}</a>
                </li>
              )}
              {settings?.email && (
                <li className="flex items-center gap-3">
                  <IconMail size={18} className="text-gray-500" /> 
                  <a href={`mailto:${settings.email}`} className="hover:text-white transition-colors">{settings.email}</a>
                </li>
              )}
              {settings?.address && (
                <li className="flex items-start gap-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {settings?.map_link ? (
                    <a href={settings.map_link} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">{settings.address}</a>
                  ) : (
                    <span>{settings.address}</span>
                  )}
                </li>
              )}
            </ul>
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto text-center text-gray-500 text-sm">
          &copy; {new Date().getFullYear()} {settings?.site_name || 'Website'}. All rights reserved.
        </div>
      </footer>

      {/* Floating Chat Widget */}
      <ChatWidget />
    </div>
  );
}
